import { StatePanel } from "@moneymap/ui";
import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { publicEnvironment } from "../env";

type AuthStatus = "loading" | "authenticated" | "anonymous" | "unconfigured";

type AuthContextValue = {
  status: AuthStatus;
  accessToken: string | null;
  email: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ requiresEmailConfirmation: boolean }>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  reauthenticate: (password: string) => Promise<string>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function createSupabaseBrowserClient(): SupabaseClient | null {
  if (!publicEnvironment.supabaseUrl || !publicEnvironment.supabaseAnonKey) return null;
  return createClient(publicEnvironment.supabaseUrl, publicEnvironment.supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: "moneymap.auth",
    },
  });
}

function isSyntheticE2eSession(): boolean {
  return (
    publicEnvironment.isE2eAuthBypassAllowed &&
    window.localStorage.getItem("moneymap:e2e-session") === "authenticated"
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useMemo(createSupabaseBrowserClient, []);
  const [session, setSession] = useState<Session | null>(null);
  const [isSyntheticSession, setIsSyntheticSession] = useState(false);
  const [status, setStatus] = useState<AuthStatus>("loading");

  useEffect(() => {
    if (isSyntheticE2eSession()) {
      setIsSyntheticSession(true);
      setStatus("authenticated");
      return undefined;
    }
    if (!client) {
      setStatus("unconfigured");
      return undefined;
    }
    let isMounted = true;
    void client.auth.getSession().then(({ data, error }) => {
      if (!isMounted) return;
      if (error) {
        setStatus("anonymous");
        return;
      }
      setSession(data.session);
      setStatus(data.session ? "authenticated" : "anonymous");
    });
    const { data } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (!isMounted) return;
      setSession(nextSession);
      setStatus(nextSession ? "authenticated" : "anonymous");
    });
    return () => {
      isMounted = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (publicEnvironment.isE2eAuthBypassAllowed && email.endsWith("@example.test")) {
        window.localStorage.setItem("moneymap:e2e-session", "authenticated");
        setIsSyntheticSession(true);
        setStatus("authenticated");
        return;
      }
      if (!client) throw new Error("Authentication is not configured for this environment.");
      const { error } = await client.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);
    },
    [client],
  );

  const signUp = useCallback(
    async (email: string, password: string) => {
      if (publicEnvironment.isE2eAuthBypassAllowed && email.endsWith("@example.test")) {
        window.localStorage.setItem("moneymap:e2e-session", "authenticated");
        setIsSyntheticSession(true);
        setStatus("authenticated");
        return { requiresEmailConfirmation: false };
      }
      if (!client) throw new Error("Authentication is not configured for this environment.");
      const { data, error } = await client.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin + "/" },
      });
      if (error) throw new Error(error.message);
      return { requiresEmailConfirmation: !data.session };
    },
    [client],
  );

  const sendPasswordReset = useCallback(
    async (email: string) => {
      if (publicEnvironment.isE2eAuthBypassAllowed && email.endsWith("@example.test")) return;
      if (!client) throw new Error("Authentication is not configured for this environment.");
      const { error } = await client.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (error) throw new Error(error.message);
    },
    [client],
  );

  const updatePassword = useCallback(
    async (password: string) => {
      if (publicEnvironment.isE2eAuthBypassAllowed && isSyntheticSession) {
        return;
      }
      if (!client) throw new Error("Authentication is not configured for this environment.");
      const { error } = await client.auth.updateUser({ password });
      if (error) throw new Error(error.message);
    },
    [client, isSyntheticSession],
  );

  const reauthenticate = useCallback(
    async (password: string) => {
      if (publicEnvironment.isE2eAuthBypassAllowed && isSyntheticSession) {
        return "synthetic-e2e-token";
      }
      if (!client || !session?.user.email) {
        throw new Error("Authentication is not configured for this session.");
      }
      const { data, error } = await client.auth.signInWithPassword({
        email: session.user.email,
        password,
      });
      if (error) throw new Error(error.message);
      setSession(data.session);
      if (!data.session) throw new Error("Recent authentication did not create a session.");
      return data.session.access_token;
    },
    [client, isSyntheticSession, session?.user.email],
  );

  const signOut = useCallback(async () => {
    if (isSyntheticSession) {
      window.localStorage.removeItem("moneymap:e2e-session");
      setIsSyntheticSession(false);
      setStatus("anonymous");
      return;
    }
    if (client) await client.auth.signOut();
  }, [client, isSyntheticSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      accessToken: isSyntheticSession ? "synthetic-e2e-token" : (session?.access_token ?? null),
      email: isSyntheticSession ? "synthetic.user@example.test" : (session?.user.email ?? null),
      signIn,
      signUp,
      sendPasswordReset,
      updatePassword,
      reauthenticate,
      signOut,
    }),
    [
      status,
      isSyntheticSession,
      session,
      signIn,
      signUp,
      sendPasswordReset,
      updatePassword,
      reauthenticate,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}

export function SessionGuard({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
      <StatePanel title="Opening your map" kind="loading">
        Checking your secure session.
      </StatePanel>
    );
  }
  if (status === "unconfigured") {
    return (
      <StatePanel title="Authentication needs configuration" kind="error">
        Set the public Supabase URL and anonymous key for this environment. No service-role key
        belongs in the browser.
      </StatePanel>
    );
  }
  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export function AnonymousOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "authenticated") return <Navigate to="/" replace />;
  return <>{children}</>;
}
